import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Palette, Upload, Image as ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BrandingSettings() {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    brand_name: "",
    brand_logo: "",
    primary_color: "#4F46E5",
    secondary_color: "#2563EB",
    accent_color: "#3B82F6",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    fetchBrandingSettings();
  }, []);

  const fetchBrandingSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings/branding`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data) {
        console.log("Fetched branding settings:", response.data);
        
        setFormData({
          brand_name: response.data.brand_name || "",
          brand_logo: response.data.brand_logo || "",
          primary_color: response.data.primary_color || "#4F46E5",
          secondary_color: response.data.secondary_color || "#2563EB",
          accent_color: response.data.accent_color || "#3B82F6",
        });
        
        if (response.data.brand_logo) {
          // Handle both old (/uploads/...) and new (/api/uploads/...) URL formats
          let logoUrl = response.data.brand_logo;
          if (logoUrl.startsWith('/uploads/') && !logoUrl.startsWith('/api/uploads/')) {
            logoUrl = `/api${logoUrl}`;
          }
          const fullLogoUrl = `${BACKEND_URL}${logoUrl}`;
          console.log("Logo URL:", fullLogoUrl);
          setLogoPreview(fullLogoUrl);
        }
      }
    } catch (error) {
      console.error("Failed to fetch branding settings:", error);
      // Don't show error toast as settings might not exist yet
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please upload an image file");
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }
      
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error("Please select a logo file first");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formDataUpload = new FormData();
      formDataUpload.append("logo", logoFile);

      const response = await axios.post(`${API}/settings/branding/upload-logo`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const logoUrl = response.data.logo_url;
      setFormData({ ...formData, brand_logo: logoUrl });
      setLogoPreview(`${BACKEND_URL}${logoUrl}`);
      toast.success("Logo uploaded successfully");
      setLogoFile(null);
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/settings/branding`,
        {
          brand_name: formData.brand_name,
          brand_logo: formData.brand_logo,
          primary_color: formData.primary_color,
          secondary_color: formData.secondary_color,
          accent_color: formData.accent_color,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Branding settings saved successfully");
      await fetchBrandingSettings();
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Failed to save branding settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Branding Settings</h1>
        <p className="text-slate-600">Customize your brand identity across the platform</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Brand Logo Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-blue-600" />
              Brand Logo
            </CardTitle>
            <CardDescription>
              Upload your brand logo (Recommended: 512x152 pixels, PNG format)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Logo Preview */}
            {logoPreview && (
              <div className="border rounded-lg p-4 bg-slate-50 flex items-center justify-center">
                <img
                  src={logoPreview}
                  alt="Brand Logo Preview"
                  className="max-h-32 object-contain"
                />
              </div>
            )}

            {/* Upload Input */}
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label htmlFor="logo-upload" className="sr-only">
                  Upload Logo
                </Label>
                <Input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="cursor-pointer"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Recommended: 512x152px PNG. Max size: 5MB
                </p>
              </div>
              {logoFile && (
                <Button
                  type="button"
                  onClick={handleLogoUpload}
                  disabled={uploading}
                  variant="outline"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Brand Name Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-purple-600" />
              Brand Name
            </CardTitle>
            <CardDescription>
              Your brand or company name displayed across the platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="brand_name">Brand Name</Label>
              <Input
                id="brand_name"
                name="brand_name"
                type="text"
                placeholder="Enter your brand name"
                value={formData.brand_name}
                onChange={handleChange}
                maxLength={50}
              />
              <p className="text-xs text-slate-500">
                This will be displayed in headers, emails, and public-facing pages
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Brand Colors Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-indigo-600" />
              Brand Colors
            </CardTitle>
            <CardDescription>
              Define your brand color palette
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Primary Color */}
            <div className="space-y-2">
              <Label htmlFor="primary_color">Primary Color</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="primary_color"
                  name="primary_color"
                  type="color"
                  value={formData.primary_color}
                  onChange={handleChange}
                  className="w-20 h-10 cursor-pointer"
                />
                <Input
                  type="text"
                  value={formData.primary_color}
                  onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                  placeholder="#4F46E5"
                  className="flex-1 font-mono"
                />
                <div 
                  className="w-10 h-10 rounded border-2 border-slate-200"
                  style={{ backgroundColor: formData.primary_color }}
                />
              </div>
              <p className="text-xs text-slate-500">Main brand color used for buttons and accents</p>
            </div>

            {/* Secondary Color */}
            <div className="space-y-2">
              <Label htmlFor="secondary_color">Secondary Color</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="secondary_color"
                  name="secondary_color"
                  type="color"
                  value={formData.secondary_color}
                  onChange={handleChange}
                  className="w-20 h-10 cursor-pointer"
                />
                <Input
                  type="text"
                  value={formData.secondary_color}
                  onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                  placeholder="#2563EB"
                  className="flex-1 font-mono"
                />
                <div 
                  className="w-10 h-10 rounded border-2 border-slate-200"
                  style={{ backgroundColor: formData.secondary_color }}
                />
              </div>
              <p className="text-xs text-slate-500">Supporting color for links and secondary elements</p>
            </div>

            {/* Accent Color */}
            <div className="space-y-2">
              <Label htmlFor="accent_color">Accent Color</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="accent_color"
                  name="accent_color"
                  type="color"
                  value={formData.accent_color}
                  onChange={handleChange}
                  className="w-20 h-10 cursor-pointer"
                />
                <Input
                  type="text"
                  value={formData.accent_color}
                  onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                  placeholder="#3B82F6"
                  className="flex-1 font-mono"
                />
                <div 
                  className="w-10 h-10 rounded border-2 border-slate-200"
                  style={{ backgroundColor: formData.accent_color }}
                />
              </div>
              <p className="text-xs text-slate-500">Highlight color for notifications and special elements</p>
            </div>

            {/* Color Preview */}
            <div className="mt-6 p-4 border rounded-lg bg-slate-50">
              <h4 className="text-sm font-semibold mb-3">Color Preview</h4>
              <div className="flex gap-3">
                <div className="flex-1 text-center">
                  <div 
                    className="h-20 rounded-lg mb-2" 
                    style={{ backgroundColor: formData.primary_color }}
                  />
                  <p className="text-xs text-slate-600">Primary</p>
                </div>
                <div className="flex-1 text-center">
                  <div 
                    className="h-20 rounded-lg mb-2" 
                    style={{ backgroundColor: formData.secondary_color }}
                  />
                  <p className="text-xs text-slate-600">Secondary</p>
                </div>
                <div className="flex-1 text-center">
                  <div 
                    className="h-20 rounded-lg mb-2" 
                    style={{ backgroundColor: formData.accent_color }}
                  />
                  <p className="text-xs text-slate-600">Accent</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={loading} size="lg">
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Branding Settings
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
